/**
 * Segmented parts (pedagogy spec §4.3.4, B4) as pure functions: which part is active, the
 * per-part ratings, the chained-part note, and the final `/answer` body of an item whose parts
 * were checked one at a time. `SessionPage` calls them; `segmented.test.ts` covers them.
 */
import type { ConfidenceLevel } from "@shared/attemptAnswer";
import type { PartCheck } from "./session";
import type { AnswerBody, AnswerOrigin } from "./types";

/** Per-viewer switch to today's single submit (`localStorage`, every access guarded). */
export const SHOW_ALL_PARTS_KEY = "mf.practice.showAllParts";

export function loadShowAllParts(): boolean {
  try {
    return window.localStorage.getItem(SHOW_ALL_PARTS_KEY) === "1";
  } catch {
    return false;
  }
}

export function saveShowAllParts(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(SHOW_ALL_PARTS_KEY, "1");
    else window.localStorage.removeItem(SHOW_ALL_PARTS_KEY);
  } catch {
    /* storage blocked: the switch still works for this visit */
  }
}

/** True for the items that are checked part by part: numeric with more than one part. */
export function isSegmentable(question: { type: string; parts?: readonly unknown[] }): boolean {
  return question.type === "numeric" && (question.parts?.length ?? 0) > 1;
}

type PartLike = { id: string };

/** The first part that has not been checked, or `undefined` once every part has. */
export function activePart<P extends PartLike>(
  parts: readonly P[],
  checks: Readonly<Record<string, PartCheck>> | undefined,
): P | undefined {
  return parts.find((part) => checks?.[part.id] === undefined);
}

export function checkedCount(
  parts: readonly PartLike[],
  checks: Readonly<Record<string, PartCheck>> | undefined,
): number {
  return parts.filter((part) => checks?.[part.id] !== undefined).length;
}

/**
 * The nearest earlier wrong part: a later part may build on its value (chained parts), so the
 * student is told to correct both on the next try. `undefined` when every earlier part is right.
 */
export function dependsOnWrong(
  parts: readonly PartLike[],
  checks: Readonly<Record<string, PartCheck>> | undefined,
  partId: string,
): string | undefined {
  const at = parts.findIndex((part) => part.id === partId);
  for (let i = at - 1; i >= 0; i--) {
    const earlier = parts[i];
    if (earlier && checks?.[earlier.id]?.outcome === "wrong") return earlier.id;
  }
  return undefined;
}

/** The lowest rating; `/answer` stores it as the item's `confidence` (§13 Q5). */
export function minConfidence(ratings: readonly ConfidenceLevel[]): ConfidenceLevel | undefined {
  return ratings.length === 0 ? undefined : (Math.min(...ratings) as ConfidenceLevel);
}

export interface SegmentedDraft {
  /** The unchecked part the student has typed into and rated, sent by "Rätta resten nu". */
  partId: string;
  value: string;
  confidence: ConfidenceLevel;
}

/**
 * The `/answer` body of a segmented item: every checked part's first-try value, `segmented: true`
 * and the per-part ratings. Unchecked parts are left out, so they grade as empty (wrong), the same
 * rule as a blank part today. `offerRetry: false` is for leaving a half-checked item, where the
 * solution is not wanted at once.
 */
export function segmentedBody(input: {
  questionId: string;
  checks: Readonly<Record<string, PartCheck>>;
  /** A typed, rated part that was not checked on its own ("Rätta resten nu"). */
  draft?: SegmentedDraft;
  hintsUsed: number;
  plan?: { topicId: string };
  offerRetry?: boolean;
  origin?: AnswerOrigin;
  /** The follow-up fields of an inserted item (`followUpBody`), sent with the first try. */
  followUp?: Pick<AnswerBody, "followUp" | "prediction">;
}): AnswerBody {
  const numeric: Record<string, string> = {};
  const partConfidence: Record<string, ConfidenceLevel> = {};
  for (const [partId, check] of Object.entries(input.checks)) {
    numeric[partId] = check.value;
    partConfidence[partId] = check.confidence;
  }
  if (input.draft) {
    numeric[input.draft.partId] = input.draft.value;
    partConfidence[input.draft.partId] = input.draft.confidence;
  }
  const confidence = minConfidence(Object.values(partConfidence));
  return {
    questionId: input.questionId,
    answer: { numeric, hintsUsed: input.hintsUsed },
    ...(confidence !== undefined ? { confidence, partConfidence } : {}),
    segmented: true,
    ...(input.plan ? { plan: input.plan } : {}),
    ...(input.offerRetry === false ? { offerRetry: false } : {}),
    ...(input.origin ? { origin: input.origin } : {}),
    ...(input.followUp ?? {}),
  };
}
